import React, { useEffect, useState }  from "react";
import { 
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner, } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from 'recoil';
import { alertState } from 'src/recoil/atoms/global';
import { SERVER_STATUS } from "src/constant/common";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getServerStatus, rebootServer } from "src/services/auth";

const ModalServerStatusCheck = ({toggle, closeModal}) => {
  const { t } = useTranslation("topbar");
  const showAlert = useSetRecoilState(alertState);
  const [serverHealthCheck, setServerHealthCheck] = useState(null);
  const [serverState, setServerState] = useState(null);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);

  const { data: serverStatus, isFetching, isError } = useQuery({
    queryKey: ["serverStatus", toggle],
    queryFn: () => getServerStatus(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: !!toggle,
  });

  const rebootServerMutation = useMutation({
    mutationFn: () => rebootServer(),
  });

  const isRunning = serverState === SERVER_STATUS.RUNNING && serverHealthCheck === SERVER_STATUS.RUNNING;
  const isRebooting = serverState !== SERVER_STATUS.RUNNING && serverHealthCheck === SERVER_STATUS.DOWN;

  useEffect(() => {
    if (!serverStatus) return;
    setServerState(serverStatus?.state);
    setServerHealthCheck(serverStatus?.health_command_status);
  }, [serverStatus]);
  
  useEffect(() => {
    if (!isError) return;
    closeModal();
  }, [isError]);

  const handleRebootServer = () => {
    rebootServerMutation.mutate();
    showAlert({
      type: "success",
      message: t("message.reboot.success"),
    });
    setIsOpenConfirm(false);
    closeModal();
  }

  if (isError) {
    return null;
  }

  return (
    <>
      <Modal centered isOpen={toggle} toggle={closeModal} backdrop="static" size="md">
        <ModalHeader toggle={closeModal} className="bg-skin-primary text-white">
          <div className="fs-6">
            {t("modal.reboot.title")}
          </div>
        </ModalHeader>
        <ModalBody className="overflow-auto">
          {
            isFetching ? (
              <div className="w-full d-flex align-items-center justify-content-center min-height-by-px-150">
                <Spinner className="f-s-16" color="primary" />
              </div>
            ) : (
              <div className="w-full d-flex flex-column gap-2 align-items-start justify-content-center p-l-85">
                <div className="d-flex gap-2 align-items-center">
                  {isRunning ? (
                    <i className="bi bi-check-circle-fill text-success f-s-100" />
                  ): (
                    <i className="bi bi-x-circle-fill text-danger f-s-100" />
                  )}
                  <div className="d-flex flex-column">
                    <div className="f-s-16 text-black-50">{t("serverStatus")}</div>
                    <div className="d-flex align-items-center justify-content-center min-height-by-px-60">
                      <span className="f-s-40 text-uppercase">{serverHealthCheck}</span>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center mb-3">
          {
            isRunning && !isFetching && (
              <Button
                className="btn btn-sm btn-secondary m-x-10 width-by-px-120"
                onClick={closeModal}
              >
                {t("button.cancel")}
              </Button>
            )
          }
          {
            !isRunning && !isFetching && (
              <Button
                className="btn btn-sm btn-danger m-x-10 width-by-px-120"
                onClick={() => setIsOpenConfirm(true)}
                disabled={isRebooting || rebootServerMutation.isPending}
              >
                {isRebooting ? t("button.rebooting") : t("button.reboot")}
              </Button>
            )
          }
        </ModalFooter>
      </Modal>
      <Modal centered isOpen={isOpenConfirm} toggle={() => setIsOpenConfirm(false)} backdrop="static" size="md">
        <ModalHeader toggle={() => setIsOpenConfirm(false)} className="bg-skin-primary text-white">
        </ModalHeader>
        <ModalBody>
          <div className="w-full d-flex flex-column gap-2 align-items-center justify-content-center">
            <span className="fw-bold f-s-20">{t("modal.confirm.title")}</span>
            <span className="text-break-space text-center">{t("modal.confirm.content")}</span>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center mb-3">
          <Button
            className="btn btn-sm btn-secondary m-x-10 width-by-px-120"
            onClick={() => setIsOpenConfirm(false)}
          >
            {t("button.no")}
          </Button>
          <Button
            className="btn btn-sm btn-danger m-x-10 width-by-px-120"
            disabled={rebootServerMutation.isPending}
            onClick={handleRebootServer}
          >
            {t("button.yes")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ModalServerStatusCheck;
